/**
 * Lightweight action breadcrumb tracker. Maintains a small ring buffer of
 * the most recent user actions (clicks, route changes, form submits) so
 * that every outgoing API span can carry the actions that led up to it.
 *
 * Capture is best-effort and intentionally cheap: only a label and a
 * timestamp per action. No DOM serialization, no element selectors, no
 * value capture from inputs.
 */

export interface ActionBreadcrumb {
  /** ms since UNIX epoch */
  ts: number;
  /** `click` | `nav` | `submit` */
  type: 'click' | 'nav' | 'submit';
  /** short human-readable label, max 80 chars */
  label: string;
}

const MAX_BREADCRUMBS = 12;
const buffer: ActionBreadcrumb[] = [];
let installed = false;

function push(type: ActionBreadcrumb['type'], label: string): void {
  buffer.push({ ts: Date.now(), type, label: label.slice(0, 80) });
  if (buffer.length > MAX_BREADCRUMBS) {
    buffer.shift();
  }
}

function labelForElement(el: Element | null): string {
  if (!el) return '(unknown)';
  const trackId = el.getAttribute('data-track-id');
  if (trackId) return trackId;
  const aria = el.getAttribute('aria-label');
  if (aria) return aria;
  const text = (el as HTMLElement).innerText;
  if (text && text.trim()) return text.trim();
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  return `${tag}${id}`;
}

function onClick(ev: Event): void {
  const target = ev.target as Element | null;
  push('click', labelForElement(target));
}

function onSubmit(ev: Event): void {
  const form = ev.target as HTMLFormElement | null;
  const name = form?.getAttribute('name') || form?.id || '(form)';
  push('submit', name);
}

function onNav(): void {
  if (typeof window === 'undefined' || !window.location) return;
  push('nav', window.location.pathname + window.location.search);
}

/**
 * Returns a snapshot of the current breadcrumb ring, oldest first. Safe to
 * call from any context; returns a shallow copy so callers can mutate.
 */
export function snapshotBreadcrumbs(): ActionBreadcrumb[] {
  return buffer.slice();
}

/**
 * Installs window-level listeners for clicks, submits, and history changes.
 * Idempotent: a second call is a no-op.
 */
export function installActionTracker(): void {
  if (installed) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;

  window.document.addEventListener('click', onClick, { capture: true, passive: true });
  window.document.addEventListener('submit', onSubmit, { capture: true, passive: true });
  window.addEventListener('popstate', onNav);

  // Patch history.pushState / replaceState so SPA route changes show up as
  // `nav` breadcrumbs. Both methods are synchronous and return void; we
  // just call through, then push our breadcrumb.
  const h = window.history;
  const origPush = h.pushState.bind(h);
  const origReplace = h.replaceState.bind(h);
  h.pushState = function patchedPushState(...args) {
    const result = origPush(...(args as Parameters<typeof origPush>));
    onNav();
    return result;
  };
  h.replaceState = function patchedReplaceState(...args) {
    const result = origReplace(...(args as Parameters<typeof origReplace>));
    onNav();
    return result;
  };

  onNav();
}
